import React from 'react';
import { Text, Spinner } from '@wix/wix-ui-tpa/cssVars';
import { useSettings } from '@wix/tpa-settings/react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { AvailabilityStatus } from '@wix/ambassador-restaurants-menu-settings-v1-menu-ordering-settings/types';
import { SchedulingType } from '@wix/ambassador-restaurants-operations-v1-operation/types';

import {
  MenuItem,
  PopulatedMenuSection,
  PricingType,
} from '../../../../types/menu';
import { useGetLayoutSize } from '../../../../components-shared/LayoutSizeProvider';
import { getImageUrl } from '../../../../utils/imgUtils';
import settingsParams from '../../settingsParams';
import { ITEM_LAYOUT_TYPE } from '../../constants';
import DishItem from '../DishItem';
import EmptyState from '../EmptyState';
import { useOnlineOrdersStorage } from '../../storage';
import { RequestStatus } from '../../../../utils/wrapRequest';
import {
  getAvailabilityStatusText,
  getItemAvailabilityPlacementId,
  sectionAvailabilityStatusKeys,
} from '../../../../utils/menuAvailability';
import type { ItemAvailabilityByPlacementId } from '../../../../utils/menuAvailability';
import { DEFAULT_TIMEZONE } from '../../../../utils/constants';
import { DispatchType } from '../../../../types/businessTypes';
import AvailabilityNotification from '../AvailabilityNotification';
import {
  isAcceptingOrdersForDispatch,
  isAddressSelectionRequired,
} from '../../../../utils/fulfillmentGating';

import { classes, st } from './MenuSection.st.css';
import {
  menuSectionDataHooks,
  SECTION_IMAGE_WIDTH,
  SECTION_IMAGE_HEIGHT,
} from './constants';

type MenuSectionProps = {
  section: PopulatedMenuSection;
  menuId: string;
  currentSectionNumber: number;
  itemAvailabilityByPlacementId: ItemAvailabilityByPlacementId;
  className?: string;
};

const sectionAvailabilityButtonKeyByDispatchType: Record<DispatchType, string> =
  {
    [DispatchType.PICKUP]:
      'section.sectionAvailabilityStatus.nextAvailable.pickup.button',
    [DispatchType.DELIVERY]:
      'section.sectionAvailabilityStatus.nextAvailable.delivery.button',
    [DispatchType.DINE_IN]:
      'section.sectionAvailabilityStatus.nextAvailable.dine_in.button',
  };

const MenuSection: React.FC<MenuSectionProps> = ({
  section,
  menuId,
  currentSectionNumber,
  itemAvailabilityByPlacementId,
  className,
}) => {
  const { t } = useTranslation();
  const { layoutSize } = useGetLayoutSize();
  const {
    itemMap,
    onItemClick,
    quickAddToCart,
    quickRemoveFromCart,
    fetchItemsStatus,
    cartQuantities,
    connectedDiscountInfoByItemId,
    sectionAvailabilityById,
    sectionAvailabilityStatusById,
    currentOperation,
    operations,
    isLocationSelected,
    dispatchesInfo,
    selectedDispatchType,
    acceptingDispatchTypesByOperationId,
    openFulfillmentModal,
    siteLocale,
    timezone,
  } = useOnlineOrdersStorage();
  const settings = useSettings();
  const showSectionTitle = settings.get(settingsParams.showSectionTitle);
  const showSectionDescription = settings.get(
    settingsParams.showSectionDescription,
  );
  const showSectionImage = settings.get(settingsParams.showSectionImage);
  const isCardLayout =
    settings.get(settingsParams.itemLayoutType) === ITEM_LAYOUT_TYPE.CARD;

  const isItemsLoading =
    fetchItemsStatus === RequestStatus.DEFAULT ||
    fetchItemsStatus === RequestStatus.LOADING;

  const items = section.itemIds
    .map((itemId) => itemMap[itemId])
    .filter((item): item is MenuItem => !!item)
    .filter((item) => item.pricingType !== PricingType.MARKET_PRICE);

  // Delivery is selectable before an address is on file (the quick-add and
  // item-modal flows both prompt for one on click) — don't treat "no
  // address yet" the same as "not accepting orders" or the card loses its
  // only entry point into that flow.
  const canAcceptOrders =
    (!!selectedDispatchType &&
      isAddressSelectionRequired(selectedDispatchType, dispatchesInfo)) ||
    isAcceptingOrdersForDispatch(
      currentOperation,
      selectedDispatchType,
      dispatchesInfo,
    );

  const isGhostMode = operations.length > 1 && !isLocationSelected;
  const isFutureSchedulingSupported =
    currentOperation?.schedulingType === SchedulingType.PREORDER ||
    !!currentOperation?.allowAsapFutureHandling;
  const isSectionUnavailable =
    sectionAvailabilityStatusById[section.id] ===
    AvailabilityStatus.UNAVAILABLE;
  const availabilityNotificationText =
    !isGhostMode && isSectionUnavailable
      ? getAvailabilityStatusText({
          availability: sectionAvailabilityById[section.id],
          locale: siteLocale,
          timezone: timezone || DEFAULT_TIMEZONE,
          t,
          keys: sectionAvailabilityStatusKeys,
        })
      : '';

  const openFulfillmentModalFromSectionBanner = React.useCallback(() => {
    if (!currentOperation) {
      return;
    }

    openFulfillmentModal({
      operations,
      currentOperation,
      dispatchesInfo,
      selectedDispatchType,
      acceptingDispatchTypesByOperationId,
    });
  }, [
    operations,
    currentOperation,
    dispatchesInfo,
    selectedDispatchType,
    acceptingDispatchTypesByOperationId,
    openFulfillmentModal,
  ]);

  const availabilityNotificationButtonText =
    isFutureSchedulingSupported && selectedDispatchType
      ? t(sectionAvailabilityButtonKeyByDispatchType[selectedDispatchType])
      : undefined;

  const renderSectionBody = () => {
    if (isItemsLoading) {
      return (
        <EmptyState>
          <Spinner />
        </EmptyState>
      );
    }

    if (items.length === 0) {
      return <EmptyState />;
    }

    return (
      <div
        className={classes.dishGrid}
        data-hook={menuSectionDataHooks.dishGrid()}
      >
        {items.map((item) => (
          <DishItem
            key={item.id}
            item={item}
            menuId={menuId}
            sectionId={section.id}
            sectionName={section.name}
            currentSectionNumber={currentSectionNumber}
            availability={
              itemAvailabilityByPlacementId[
                getItemAvailabilityPlacementId(
                  menuId,
                  section.id,
                  item.id ?? '',
                )
              ]
            }
            onClick={onItemClick}
            onQuickAdd={quickAddToCart}
            onQuickRemove={quickRemoveFromCart}
            cartQuantity={cartQuantities[item.id ?? ''] ?? 0}
            canAcceptOrders={canAcceptOrders}
            discountLabel={connectedDiscountInfoByItemId[item.id ?? '']?.label}
            discountedPrice={
              !connectedDiscountInfoByItemId[item.id ?? '']?.hidePriceDisplay &&
              connectedDiscountInfoByItemId[item.id ?? '']?.finalPrice !== 0
                ? connectedDiscountInfoByItemId[item.id ?? '']
                    ?.formattedFinalPrice
                : undefined
            }
          />
        ))}
      </div>
    );
  };

  return (
    <div
      className={st(
        classes.root,
        { [layoutSize]: true, card: isCardLayout },
        className,
      )}
      data-hook={menuSectionDataHooks.root()}
      data-section-id={section.id}
      id={`${section.id}-tab`}
      role="tabpanel"
      aria-labelledby={section.id}
    >
      <div className={classes.header}>
        {showSectionImage && section.image?.id && (
          <div className={classes.sectionImageWrapper}>
            <img
              className={classes.sectionImage}
              src={getImageUrl(
                section.image,
                SECTION_IMAGE_WIDTH[layoutSize],
                SECTION_IMAGE_HEIGHT,
                'fit',
              )}
              alt={section.image.altText || section.name || ''}
              loading="lazy"
            />
          </div>
        )}
        {showSectionTitle && (
          <Text
            className={classes.title}
            data-hook={menuSectionDataHooks.title()}
            tagName="h3"
          >
            {section.name}
          </Text>
        )}
        {availabilityNotificationText ? (
          <AvailabilityNotification
            dataHook={menuSectionDataHooks.sectionAvailabilityNotification()}
            text={availabilityNotificationText}
            buttonText={availabilityNotificationButtonText}
            onButtonClick={
              availabilityNotificationButtonText
                ? openFulfillmentModalFromSectionBanner
                : undefined
            }
          />
        ) : null}
        {showSectionDescription && section.description && (
          <Text
            className={classes.description}
            data-hook={menuSectionDataHooks.description()}
            tagName="p"
          >
            {section.description}
          </Text>
        )}
      </div>
      {renderSectionBody()}
    </div>
  );
};

export default MenuSection;
